import { useStore } from '@/store/useStore'
import { useShallow } from 'zustand/react/shallow'
import type { LearningLayer } from '@/types'
import { getPreferredLearningStyle } from '@/entities/profile/getPreferredLearningStyle'
import { getPreferredLearnerType } from '@/entities/profile/getSocialLearningProfile'
import { createDynamicAssessmentSnapshot } from '@/entities/profile/createDynamicAssessmentSnapshot'
import type { AppState } from '@/store/rootTypes'
import { resolveEffectiveLearningLayer } from '@/store/effectiveLearningLayer'
import { getLatestCompletedDiagnosticSession } from '@/features/pretest'
import { createAbilityGrowthSnapshot } from '@/entities/profile/createAbilityGrowthSnapshot'

let cachedProfile: AppState['profile'] = null
let cachedAttempts: AppState['attempts'] | null = null
let cachedDynamicAssessment: ReturnType<typeof createDynamicAssessmentSnapshot> | null = null
let cachedGrowthProfile: AppState['profile'] = null
let cachedGrowthAttempts: AppState['attempts'] | null = null
let cachedAbilityGrowth: ReturnType<typeof createAbilityGrowthSnapshot> | null = null

function getDynamicAssessment(state: AppState) {
  if (!state.profile) return null
  if (state.profile === cachedProfile && state.attempts === cachedAttempts) return cachedDynamicAssessment
  cachedProfile = state.profile
  cachedAttempts = state.attempts
  cachedDynamicAssessment = createDynamicAssessmentSnapshot(state.profile, state.attempts)
  return cachedDynamicAssessment
}

function getAbilityGrowth(state: AppState) {
  if (!state.profile) return null
  if (state.profile === cachedGrowthProfile && state.attempts === cachedGrowthAttempts) return cachedAbilityGrowth
  cachedGrowthProfile = state.profile
  cachedGrowthAttempts = state.attempts
  cachedAbilityGrowth = createAbilityGrowthSnapshot(state.profile, state.attempts)
  return cachedAbilityGrowth
}

export function selectLearnerSnapshot(state: AppState) {
  const profile = state.profile
  const dynamicAssessment = getDynamicAssessment(state)
  const abilityGrowth = getAbilityGrowth(state)
  const latestDiagnosticResult = getLatestCompletedDiagnosticSession(state.initialProfileSessions)?.result ?? null
  const layer = resolveEffectiveLearningLayer(state, dynamicAssessment) as LearningLayer | null
  const style = profile
    ? getPreferredLearningStyle(profile.preference)
    : state.learningStyle ?? state.student.style
  const learnerType = state.learnerType ?? (profile ? getPreferredLearnerType(profile.preference) : null)

  return {
    id: profile?.identity.id ?? state.activeStudentId ?? 'legacy-student',
    profile,
    name: profile?.identity.name ?? state.student.name,
    grade: profile?.identity.grade ?? state.student.grade,
    avatar: profile?.identity.avatar ?? state.student.avatar,
    layer,
    manualLayerOverride: state.manualLayerOverride,
    currentStage: state.currentStage,
    stageDimensionRatios: state.currentStage?.dimensionRatios ?? null,
    baselineLayer: profile?.recommendedLevel ?? state.pretestLayer ?? state.student.layer,
    style,
    learnerType,
    preference: profile?.preference ?? null,
    pretestScore: profile?.pretestScore ?? state.pretestScore,
    dynamicAssessment,
    abilityGrowth,
    latestDiagnosticResult,
    streak: state.student.streak,
    totalStars: state.student.totalStars,
    completedUnits: state.student.completedUnits,
    completedLessons: state.student.completedLessons,
    hasDetailedProfile: Boolean(profile),
  }
}

/**
 * 页面层读取学习者信息的唯一入口。
 * 所有字段来自同一个 useStore；profile 为新数据权威来源，旧字段仅用于
 * 迁移历史浏览器数据和未完成前测的用户。
 */
export function useLearnerSnapshot() {
  return useStore(useShallow(selectLearnerSnapshot))
}
